import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Button } from "components/DesignSystem/Button/Button";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { USD } from "constants/currency";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { useWriteOffInvoicePendingAmountMutation } from "store/apis/invoices";
import { Invoice } from "types/Models/invoice";
import { BackendError } from "types/utils/error";
import { currency } from "utils/Currency";
import { WriteOffDateField } from "./WriteOffDateField";

type WriteOffPendingAmountModalProps = {
  invoice: Invoice;
  open: boolean;
  onClose: () => void;
};

type WriteOffFormValues = {
  written_off_at: string;
};

const getInvoiceTotalInUsd = (invoice: Invoice) => {
  if (!invoice.currency || invoice.currency === USD) {
    return Number(invoice.total) || Number(invoice.invoice_amount) || 0;
  }
  return Number(invoice.payment_record_amount?.amount_in_base_currency || 0);
};

export const WriteOffPendingAmountModal = ({
  invoice,
  open,
  onClose,
}: WriteOffPendingAmountModalProps) => {
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const [writeOffPendingAmount] = useWriteOffInvoicePendingAmountMutation();

  const pendingAmount = Number(invoice.pending_amount || 0);
  const invoiceTotal = getInvoiceTotalInUsd(invoice);
  const paidAmount = Math.max(invoiceTotal - pendingAmount, 0);

  const initialValues: WriteOffFormValues = {
    written_off_at: dayjs().format(YYYY_MM_DD),
  };

  const handleSubmit = async (values: WriteOffFormValues) => {
    try {
      await writeOffPendingAmount({
        entityId,
        invoiceId: invoice.uuid,
        writtenOffAt: values.written_off_at,
      }).unwrap();
      successToast({
        message: `${currency({ amount: pendingAmount })} written off. ${
          invoice.invoice_number
        } is now marked Paid.`,
      });
      onClose();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Something went wrong",
      });
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Content>
        <FormikForm initialValues={initialValues} onSubmit={handleSubmit}>
          {({ isSubmitting }) => (
            <>
              <Modal.Header>
                <Modal.Title>Write off pending amount</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body className="t-flex t-flex-col t-gap-5 t-text-body-sm t-text-text-60">
                <div className="t-border-solid t-border t-border-neutral-0 t-rounded-lg t-py-4 t-px-5 t-flex t-gap-6">
                  <InfoItem label="Invoice number">
                    <span className="t-font-semibold">
                      {invoice.invoice_number}
                    </span>
                  </InfoItem>
                  <InfoItem label="Invoice total">
                    <AmountSuperScript amount={invoiceTotal} />
                  </InfoItem>
                  <InfoItem label="Paid so far">
                    <AmountSuperScript amount={paidAmount} />
                  </InfoItem>
                  <InfoItem label="To be written off">
                    <span className="t-font-semibold">
                      <AmountSuperScript amount={pendingAmount} />
                    </span>
                  </InfoItem>
                </div>

                <p className="t-m-0 t-text-text-100">
                  The customer has paid{" "}
                  <AmountSuperScript amount={paidAmount} /> of{" "}
                  {invoice.invoice_number}. The remaining{" "}
                  <span className="t-font-semibold">
                    <AmountSuperScript amount={pendingAmount} />
                  </span>{" "}
                  will be written off as uncollectible.
                </p>

                <div className="t-flex t-flex-col t-gap-1">
                  <span className="t-font-medium t-text-text-100">
                    What will happen
                  </span>
                  <ul className="t-m-0 t-pl-5 t-list-disc t-flex t-flex-col t-gap-1">
                    <li>
                      A journal entry is created on the date below, debiting Bad
                      Debt and crediting Accounts Receivable by{" "}
                      <AmountSuperScript amount={pendingAmount} />.
                    </li>
                    <li>
                      The invoice is marked Paid and no longer shows an amount
                      due.
                    </li>
                    <li>The payments already recorded are not changed.</li>
                  </ul>
                </div>

                <WriteOffDateField
                  name="written_off_at"
                  amount={pendingAmount}
                  invoiceDate={invoice.invoice_date}
                />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button">Cancel</Button>
                </Modal.RawClose>
                <Button
                  type="submit"
                  customType="primary"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Write off {currency({ amount: pendingAmount })}
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
